import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RootState } from "us.helper/types";
import * as Actions from "actions";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { $Button, $Message, $Modal } from "us.common/components";
import {
  generateTemplateJSON,
  getEditStatus,
} from "components/TemplateEditor/Functions";
import { generatePreviewImage } from "functions/TemplateEditor";
import { QuestionCircleOutlined } from "us.icons";
import {
  isTemplateNameChanged,
  isValidTemplate,
} from "components/HeaderExtra/Functions";
import { getCurrentUserEmail } from "us.helper/utility";
import * as Azure from "us.helper/azure";
import { getUpdatedVersion } from "functions/TemplateHistory";
import { TemplateVersion } from "components/TemplateEditor/Components/VersionHistory/Constants";

/**
 * @description - Navigation confirmation when user exits from the editor.
 */
const NavigatePrompt: React.FC<PropsFromRedux> = (props) => {
  const {
    template,
    initTemplate,
    confirmExitModal,
    templates,
    templateVersions,
    AuthorizationProfile,
    handleExitModal,
    saveTemplate,
  } = props;
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [pagesElement, setPagesElement] = useState<any>(null);

  const initDocumentData = React.useRef<any>(null);

  const exitMessage = t(
    "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CHANGES_YOU_MADE_MAY_NOT_BE_SAVED"
  );

  const isDocumentChanged = useMemo(() => {
    return getEditStatus(pagesElement, initDocumentData, "navigate");
  }, [pagesElement, initDocumentData]);

  useEffect(() => {
    if (confirmExitModal && !confirmExitModal.visible) {
      const { location } = confirmExitModal ?? {};
      if (location && location?.pathname) {
        navigate(`${location?.pathname}${location?.search}`, location?.state);
        handleExitModal && handleExitModal({ visible: false });
      }
    }
  }, [confirmExitModal]);

  useEffect(() => {
  //   const unblock = history.block((location: any, action: any) => {
  //     if ((action == "POP" || action == "PUSH") && isDocumentChanged) {
  //       handleExitModal && handleExitModal({ location, visible: true });
  //       return false;
  //     }
  //     return true;
  //   });

  //   return () => {
  //     unblock();
  //   };
  // }, [isDocumentChanged, confirmExitModal, history]);

  
  const handleBlockNavigation = (event: BeforeUnloadEvent) => {
    if (isDocumentChanged) {
      event.preventDefault();
      event.returnValue = exitMessage;
    }
  };

  window.addEventListener("beforeunload", handleBlockNavigation);

  return () => {
    window.removeEventListener("beforeunload", handleBlockNavigation);
  };
}, [isDocumentChanged, exitMessage]);

  useEffect(() => {
    setTimeout(() => {
      const containerElement = document.getElementById("pages-container");
      initDocumentData.current = containerElement?.outerHTML;
      setPagesElement(containerElement?.outerHTML);
    }, 100);

    const onCloseTab = (e: any) => {
      e.preventDefault();
      if (getEditStatus(pagesElement, initDocumentData, undefined)) {
        return (e.returnValue = exitMessage);
      }
    };
    window.removeEventListener("beforeunload", onCloseTab);
    window.addEventListener("beforeunload", onCloseTab);
    return () => {
      window.removeEventListener("beforeunload", onCloseTab);
    };
  }, [initTemplate]);

  const updatedUser = useMemo(() => {
    const currentUser: any = new Azure.ADAuth().currentUser();
    return getCurrentUserEmail(
      currentUser,
      AuthorizationProfile?.authorizationProfile
    );
  }, [AuthorizationProfile]);

  const getVersionNo = useCallback(
    (versions: Array<TemplateVersion>, template: any) => {
      return getUpdatedVersion(versions, template);
    },
    [initTemplate, templateVersions]
  );

  /**
   * @description - Handle save template button click event
   */
  const onSaveTemplate = async () => {
    const templateJson = generateTemplateJSON(template.data);
    if (templateJson && isValidTemplate()) {
      const parsedTemplateJson = JSON.parse(templateJson);
      try {
        const previewImage = await generatePreviewImage();
        let versionNo = getVersionNo(
          templateVersions?.data,
          parsedTemplateJson
        );
        saveTemplate &&
          saveTemplate({
            saveParams: {
              ...parsedTemplateJson,
              templateContent: {
                ...parsedTemplateJson?.templateContent,
                previewImage,
                versionNo,
                updatedUser,
              },
              isTemplateNameChanged: isTemplateNameChanged(
                template?.data,
                templates?.data
              ),
            },
          });
      } catch (e) {
        saveTemplate &&
          saveTemplate({
            saveParams: {
              ...parsedTemplateJson,
              templateContent: {
                ...parsedTemplateJson?.templateContent,
                versionNo: 1,
                updatedUser,
              },
              isTemplateNameChanged: false,
            },
          });
      }
    } else {
      $Message.error(
        t(
          "US.USC.TEMPLATEEDITOR:VALIDATIONS.PLEASE_SET_UP_THE_REPEATING_ADAPTER_TO_YOUR_TEMPLATE_FOR_YOUR_REPEATING_TOKENS"
        )
      );
    }
  };

  return (
    <$Modal
      open={confirmExitModal?.visible}
      footer={null}
      centered
      onCancel={() => handleExitModal && handleExitModal({ visible: false })}
    >
      <div>
        <div className="d-flex">
          <h1 className="mr-3">
            <QuestionCircleOutlined className="text-warning" />
          </h1>
          <p className="mb-4 mt-2">{exitMessage}.</p>
        </div>
        <div className="d-flex justify-content-end">
          <$Button
            className="mr-2"
            type="primary"
            disabled={initTemplate?.saving}
            loading={initTemplate?.saving}
            onClick={() => onSaveTemplate && onSaveTemplate()}
          >
            {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.SAVE_AND_EXIT")}
          </$Button>
          <$Button
            className="mr-2"
            type="primary"
            disabled={initTemplate?.saving}
            onClick={() =>
              handleExitModal &&
              handleExitModal({ ...confirmExitModal, visible: false })
            }
          >
            {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.EXIT_WITHOUT_SAVING")}
          </$Button>
          <$Button
            disabled={initTemplate?.saving}
            onClick={() =>
              handleExitModal && handleExitModal({ visible: false })
            }
          >
            {t("COMMON.CANCEL")}
          </$Button>
        </div>
      </div>
    </$Modal>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templatesHistory, templateEditor, AuthorizationProfile } = state;
  const {
    template,
    initTemplate,
    confirmExitModal,
    templates,
    templateVersions,
  } = templatesHistory;
  const { section } = templateEditor;
  return {
    template,
    section,
    initTemplate,
    confirmExitModal,
    templates,
    templateVersions,
    AuthorizationProfile,
  };
};

const { templateData } = Actions.templatesHistory;
const mapDispatchToProps = {
  saveTemplate: templateData.save,
  handleExitModal: templateData.handleModal,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(NavigatePrompt);
